import { yupResolver } from '@hookform/resolvers/yup';
import AddOutlined from '@mui/icons-material/AddOutlined';
import { paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import { SxProps, Theme } from '@mui/system';
import {
  AddOnRateType,
  AddOnType,
  getV1CompaniesCompanyIdAddOnsTypeahead,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { NameIdSchemaRequired } from '~constants/regexConst';
import { FormStateChangeProps } from '~formsShared/types';
import { AddOnTypeahead } from '~hooks/useAddOns/models';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { Rate } from '~hooks/useRates/models';
import { testUniqueAddOnAssignments } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AddOnForm, { AddOnFormHandler } from '../../AddOnCharges/components/AddOnForm';
import { addOnRateTypeOptions } from '../../AddOnCharges/components/addOnFormSchema';
import InstallmentsFieldHelper from './InstallmentsFieldHelper';
import InstallmentsHelperText from './InstallmentsHelperText';

const addOnRateTypeOptionsList = Object.values(AddOnRateType);

export const getAddOnRateTypeOptionLabel = (item: AddOnRateType) => {
  const option = addOnRateTypeOptions.find((option) => option.id === item);

  return option?.name ?? '';
};

export type AddOnDialogState = {
  defaultAddOnName?: string;
  indexInList?: number;
  isOpen: boolean;
  isSaving?: true;
};

const constructEmptyAddOn = () => ({
  id: '',
  name: '',
  rateType: '',
  quantity: 0,
  rate: 0,
  percentage: 0,
  rateValue: 0,
  costRateValue: 0,
  addOnRateType: '',
  externalId: '',
  accountingId: '',
  totalInstallments: 0,
});

export const addOnSchema = yup.object().shape({
  id: yup.string(),
  name: yup.string(),
  rateType: yup.string(),
  quantity: yup.number(),
  percentage: yup.number(),
  rateValue: yup.number(),
  costRateValue: yup.number().notRequired(),
  externalId: yup.string().notRequired(),
  accountingId: yup.string().notRequired(),
  totalInstallments: yup.number().integer().min(0),
});

const automaticAddOnsSchema = yup.object().shape({
  addOns: yup
    .array()
    .of(
      yup
        .object()
        .shape({
          isDeleted: yup.boolean().notRequired(),
          addOn: addOnSchema.notRequired(),
        })
        .required(),
    )
    .required()
    .test('unique-add-on-assignments', '', testUniqueAddOnAssignments),
});
type AutomaticAddOnsDTO = yup.InferType<typeof automaticAddOnsSchema>;
export type AddOnDTO = yup.InferType<typeof addOnSchema>;

type AddOnSchemaRequired = yup.InferType<ReturnType<typeof addOnSchema.required>>;
export type AutomaticAddOnsHandler = {
  onSubmit: () => Promise<AddOnSchemaRequired[]>;
};

interface AutomaticAddOnsProps {
  onFormStateChange: ({ isValid, isDirty }: FormStateChangeProps) => void;
  rate?: Rate;
  hasEmptyAddOn?: boolean;
  filteredAddOns?: AddOnType[];
  sx?: SxProps<Theme>;
}

const AutomaticAddOns = forwardRef<AutomaticAddOnsHandler, AutomaticAddOnsProps>(
  function AutomaticAddOns(
    { onFormStateChange, rate, hasEmptyAddOn = false, filteredAddOns },
    ref,
  ) {
    const { getAddOn, createAddOnByCompanyId } = useAddOns();
    const { userStore, toasterStore } = useStores();

    const companyId = getEffectiveUserCompanyId(userStore);
    const initialAddOnsList = (
      (rate?.addOns ?? hasEmptyAddOn) ? [constructEmptyAddOn()] : []
    ).map((addOn) => ({
      addOn: {
        id: addOn.id,
        name: addOn.name,
        rateType: addOn.addOnRateType,
        quantity: addOn.quantity,
        rate: addOn.rate,
        percentage: addOn.percentage,
        rateValue:
          addOn.addOnRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
            ? addOn.percentage
            : addOn.rate,
        costRateValue: addOn.costRateValue,
      },
    }));

    const form = useForm<AutomaticAddOnsDTO>({
      resolver: yupResolver(automaticAddOnsSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: { addOns: initialAddOnsList },
    });

    const list = useFieldArray({ control: form.control, name: 'addOns' });
    const watchedList = form.watch('addOns');
    const listDidChange = useMemo(() => {
      const initialIds = initialAddOnsList.map((item) => item.addOn.id).toString();
      const newIds = watchedList.map((item) => item.addOn?.id ?? '').toString();

      return initialIds !== newIds;
    }, [initialAddOnsList, watchedList]);

    const addOnFormRef = useRef<AddOnFormHandler>({});
    const [addOnDialogState, setAddOnDialogState] = useState<AddOnDialogState>({
      isOpen: false,
    });

    const handleAddOnInput = useCallback(
      (value: string, indexInList: number) => {
        setAddOnDialogState((state) => ({
          ...state,
          defaultAddOnName: value,
          indexInList,
        }));
      },
      [form],
    );

    const handleCreateNewAddOnChipClick = useCallback(() => {
      setAddOnDialogState((state) => ({ ...state, isOpen: true }));
    }, []);

    const closeAddOnDialog = useCallback(() => {
      setAddOnDialogState({ isOpen: false });
    }, []);

    const handleAddOnFormSubmit = useCallback(async () => {
      const data = await addOnFormRef.current?.onSubmit?.();
      const index = addOnDialogState.indexInList;

      if (!data || isNil(index)) {
        return;
      }

      setAddOnDialogState((state) => ({ ...state, isSaving: true }));

      const newAddOn = await createAddOnByCompanyId(companyId, data);

      form.setValue(`addOns.${index}.addOn`, {
        id: String(newAddOn.id),
        name: newAddOn.name,
      });
      form.setValue(`addOns.${index}.addOn.rateType`, newAddOn.revenueCostRateType);
      form.setValue(`addOns.${index}.addOn.quantity`, newAddOn.quantity);
      form.setValue(
        `addOns.${index}.addOn.rateValue`,
        newAddOn.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
          ? newAddOn.percentage
          : (newAddOn.rate ?? undefined),
      );
      form.setValue(
        `addOns.${index}.addOn.costRateValue`,
        newAddOn.costRateValue ?? undefined,
      );
      toasterStore.push(
        alert(t('administration.rates.add_ons.add_on_created'), AlertTypes.success),
      );
      closeAddOnDialog();
    }, [addOnDialogState.indexInList, form, closeAddOnDialog]);

    const fetchAddOns = useCallback(
      async (additionalProps: { query?: string; cursor?: string } = {}) => {
        try {
          const response = await getV1CompaniesCompanyIdAddOnsTypeahead({
            path: { 'company-id': companyId },
            query: {
              'search[query]': additionalProps.query || undefined,
              'page[limit]': DEFAULT_API_PAGE_SIZE,
              'filter[add_on_types]': filteredAddOns,
              ...(additionalProps.cursor
                ? { 'page[after]': additionalProps.cursor }
                : {}),
            },
          });

          const addOns = response.data.data.map((addOn) => {
            const parsed = AddOnTypeahead.parse(addOn);

            return { id: parsed.id, name: parsed.name };
          });

          return { data: addOns, pagination: extractPagination(response) };
        } catch (error) {
          connection.handleRequestError(
            error,
            t('error_messages.add_ons.failed_to_fetch'),
          );
        }
      },
      [companyId, filteredAddOns],
    );

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve, reject) => {
            form.handleSubmit(
              (data) => {
                const prunedList = data.addOns.reduce((acc, item) => {
                  const isValid = Boolean(item.addOn?.id);
                  if (isValid && item.addOn) {
                    acc.push(item.addOn);
                  }

                  return acc;
                }, [] as AddOnDTO[]);

                resolve(prunedList);
              },
              (errors) => {
                reject(errors);
              },
            )();
          });
        },
      }),
      [form.handleSubmit],
    );

    useEffect(() => {
      if (listDidChange) {
        onFormStateChange({ isValid: true, isDirty: true });
      }
    }, [listDidChange, onFormStateChange]);

    useEffect(() => {
      const subscription = form.watch((_, { name, type }) => {
        if (type === 'change' && name?.startsWith('addOns')) {
          void form.trigger('addOns');
        }
      });
      return () => subscription.unsubscribe();
    }, [form]);

    const fillAddOnDetails = useCallback(
      async (item: NameIdSchemaRequired | null, index: number) => {
        if (item?.id) {
          const addOn = await getAddOn(companyId, item.id);

          if (addOn) {
            form.setValue(`addOns.${index}.addOn.rateType`, addOn.revenueCostRateType, {
              shouldDirty: true,
              shouldValidate: true,
              shouldTouch: true,
            });
            form.setValue(`addOns.${index}.addOn.quantity`, addOn.quantity);
            form.setValue(
              `addOns.${index}.addOn.rateValue`,
              addOn.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                ? addOn.percentage
                : (addOn.rate ?? undefined),
            );
            form.setValue(`addOns.${index}.addOn.externalId`, addOn.externalId ?? '');
            form.setValue(`addOns.${index}.addOn.accountingId`, addOn.accountingId ?? '');
            form.setValue(`addOns.${index}.addOn.name`, addOn.name);
          }

          return {
            id: addOn?.id,
            name: addOn?.name,
            rateType: addOn?.revenueCostRateType,
            quantity: addOn?.quantity,
            rate: addOn?.rate ?? undefined,
            percentage: addOn?.percentage,
            externalId: addOn?.externalId,
            accountingId: addOn?.accountingId,
            rateValue:
              addOn?.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                ? addOn?.percentage
                : (addOn?.rate ?? undefined),
            costRateValue: addOn?.costRateValue ?? undefined,
          };
        }

        return item;
      },
      [companyId, form],
    );

    let addOnNumber = 0;

    return (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {list.fields.map((item, index) => {
            if (item.isDeleted) {
              return null;
            }

            addOnNumber = addOnNumber + 1;

            return (
              <Box
                key={item.id}
                sx={(theme) => ({
                  border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                  borderRadius: 0.5,
                  p: 1.5,
                  position: 'relative',
                })}
              >
                <RemoveItemButton
                  action={() => list.remove(index)}
                  sx={{
                    cursor: 'pointer',
                    position: 'absolute',
                    right: '-10px',
                    top: '-10px',
                  }}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 4 }}>
                    <AutocompleteAsyncFormField
                      isRequired
                      disablePortal={false}
                      asyncCallback={fetchAddOns}
                      clearable
                      control={form.control}
                      errors={form.formState.errors}
                      getLabel={(item: NameIdSchemaRequired | null) =>
                        item?.id ? item?.name : ''
                      }
                      getValue={(item: NameIdSchemaRequired | null) => item?.id ?? ''}
                      label={t(
                        'administration.rates.automatic_add_ons.add_on_selector_label',
                        { number: addOnNumber },
                      )}
                      name={`addOns.${index}.addOn`}
                      noOptionsText={
                        <Chip
                          label={t('administration.rates.automatic_add_ons.new_add_on')}
                          onClick={handleCreateNewAddOnChipClick}
                          size="small"
                          sx={{ cursor: 'pointer' }}
                        />
                      }
                      onClear={() => {
                        const emptyAddOn = constructEmptyAddOn();

                        form.setValue(`addOns.${index}.addOn`, emptyAddOn);
                        form.setValue(
                          `addOns.${index}.addOn.quantity`,
                          emptyAddOn.quantity,
                        );
                        form.setValue(
                          `addOns.${index}.addOn.rateType`,
                          emptyAddOn.rateType,
                        );
                        form.setValue(
                          `addOns.${index}.addOn.rateValue`,
                          emptyAddOn.rateValue,
                        );
                        form.setValue(
                          `addOns.${index}.addOn.costRateValue`,
                          emptyAddOn.costRateValue,
                        );
                      }}
                      onInput={(_event, value) => handleAddOnInput(value, index)}
                      onSelect={(item) => fillAddOnDetails(item, index)}
                    />
                  </Grid>

                  <Grid size={{ xs: 2 }}>
                    <AutocompleteFormField
                      isRequired
                      control={form.control}
                      errors={form.formState.errors}
                      disablePortal={false}
                      getLabel={(item: AddOnRateType) =>
                        getAddOnRateTypeOptionLabel(item)
                      }
                      getValue={(item: AddOnRateType) => item}
                      label={t('administration.rates.automatic_add_ons.rate_type')}
                      list={addOnRateTypeOptionsList}
                      name={`addOns.${index}.addOn.rateType`}
                    />
                  </Grid>
                  <Grid size={{ xs: 1.5 }}>
                    <TextFormField
                      control={form.control}
                      // A percentage is taken from the settlement's own total, so
                      // the backend never multiplies it by this quantity.
                      disabled={
                        watchedList[index]?.addOn?.rateType ===
                        AddOnRateType.RATE_PERCENT_OF_TOTAL
                      }
                      errors={form.formState.errors}
                      label={t('administration.rates.automatic_add_ons.quantity')}
                      name={`addOns.${index}.addOn.quantity`}
                      type="number"
                    />
                  </Grid>

                  <Grid size={{ xs: 1.5 }}>
                    <TextFormField
                      control={form.control}
                      errors={form.formState.errors}
                      label={t('administration.rates.add_ons.cost_rate')}
                      name={`addOns.${index}.addOn.costRateValue`}
                      type="number"
                      {...(watchedList[index]?.addOn?.rateType ===
                      AddOnRateType.RATE_PERCENT_OF_TOTAL
                        ? {
                            InputProps: { endAdornment: '%' },
                            inputProps: { step: '0.0001' },
                          }
                        : {})}
                    />
                  </Grid>

                  <Grid size={{ xs: 1.5 }}>
                    <TextFormField
                      control={form.control}
                      errors={form.formState.errors}
                      label={t('administration.rates.invoice_rate')}
                      name={`addOns.${index}.addOn.rateValue`}
                      type="number"
                      {...(watchedList[index]?.addOn?.rateType ===
                      AddOnRateType.RATE_PERCENT_OF_TOTAL
                        ? {
                            InputProps: { endAdornment: '%' },
                            inputProps: { step: '0.0001' },
                          }
                        : {})}
                    />
                  </Grid>

                  <Grid size={{ xs: 1.5 }}>
                    <TextFormField
                      control={form.control}
                      errors={form.formState.errors}
                      label={t(
                        'administration.rates.automatic_add_ons.total_installments',
                      )}
                      name={`addOns.${index}.addOn.totalInstallments`}
                      type="number"
                    />
                    <InstallmentsFieldHelper
                      totalInstallments={watchedList[index]?.addOn?.totalInstallments}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <InstallmentsHelperText
                      quantity={watchedList[index]?.addOn?.quantity}
                      rateType={watchedList[index]?.addOn?.rateType}
                      rateValue={watchedList[index]?.addOn?.rateValue}
                      totalInstallments={watchedList[index]?.addOn?.totalInstallments}
                    />
                  </Grid>
                </Grid>
              </Box>
            );
          })}

          <Box>
            <SmallButton
              color="inherit"
              onClick={() => list.append({ addOn: constructEmptyAddOn() })}
              startIcon={<AddOutlined />}
              variant="outlined"
            >
              {t('administration.rates.automatic_add_ons.add_on_cta')}
            </SmallButton>
          </Box>
        </Box>

        <Dialog
          isOpen={addOnDialogState.isOpen}
          onClose={closeAddOnDialog}
          preventCloseOnBackdropClick
          sx={{ [`& .${paperClasses.root}`]: { maxWidth: '700px' } }}
        >
          <DialogHeader onClose={closeAddOnDialog}>
            {t('administration.rates.add_ons.create_add_on')}
          </DialogHeader>

          <DialogContent>
            <AddOnForm
              ref={addOnFormRef}
              defaultAddOnName={addOnDialogState.defaultAddOnName}
            />
          </DialogContent>

          <DialogActions>
            <SmallButton
              color="secondary"
              disabled={addOnDialogState.isSaving}
              onClick={closeAddOnDialog}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>

            <SmallLoadingButton
              loading={addOnDialogState.isSaving}
              onClick={handleAddOnFormSubmit}
            >
              {t('actions.create')}
            </SmallLoadingButton>
          </DialogActions>
        </Dialog>
      </>
    );
  },
);

export default AutomaticAddOns;
